import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { createFileRoute } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Food.module.scss";
import companySettingsStyles from "@/styles/CompanySettings.module.scss";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  foodQROptions,
  foodUserOptions,
  foodWalletBadgeOptions,
  snackSupplierOptions,
  useCompany,
} from "@/api";
import { Suspense, useEffect, useState } from "react";
import {
  Button,
  Icon,
  LoadingSpinner,
  PopupCard,
  ProgressIndicator,
  Stepper,
} from "@app-components";
import { calculateLunchPrice, NOK } from "@/lib";
import BalanceTopup from "@/components/food/BalanceTopup";
import { Tooltip } from "react-tooltip";
import { AnimatedTickingNumber } from "@/components/AnimatedTickingNumber";
import UserTransactionsTable from "@/components/food/UserTransactionsTable";
import { useFoodUserData } from "@/hooks/useFoodUserData";
import { useLoggedInUser, useRouterStore } from "@/store/zustandStore";
import { useLocalState } from "@/hooks/useLocalState";
import { AddToWalletButton } from "@/components/AddToWalletButton";
import { useIsMobile } from "@/hooks/useIsMobile";
import { addMonths, differenceInDays, startOfMonth } from "date-fns";
import WalletBadgeModalContent from "@/components/food/WalletBadgeModalContent";
import UsersLunchQRCard from "@/components/food/UsersLunchQRCard";

export const Route = createFileRoute("/_portal/lunch")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId, user } = useRouterStore.getState();
    if (!queryClient) return;

    queryClient.ensureQueryData(snackSupplierOptions(supplierCompanyId));
    queryClient.ensureQueryData(
      foodUserOptions(user.userId, user.buildingTenantId, supplierCompanyId),
    );
    queryClient.ensureQueryData(
      foodUserOptions(
        user.userId,
        user.buildingTenantId,
        supplierCompanyId,
        user.language,
      ),
    );
    queryClient.ensureQueryData(foodQROptions(user.userId));
  },
});

function RouteComponent() {
  useWelcomeFoodAccess(false, true);
  const { t } = useTranslation();
  const { isMobile, isDesktop } = useIsMobile();
  const user = useLoggedInUser();
  const { data: company } = useCompany(user.buildingTenantId);
  const {
    isLoadingDeals,
    isLoadingFoodUser,
    refetchFoodUser,
    usersDeals,
    usersMostExpensiveDealPrice,
    userHasFreeDeal,
    foodUser,
    hasPendingTransactions,
    hasLowBalance,
    hasCriticalBalance,
  } = useFoodUserData();
  const [introDismissed, setIntroDismissed] = useLocalState(
    "food-intro-dismissed",
    false,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [topupTooltipOpen, setTopupTooltipOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  const daysUntilNextMonth =
    differenceInDays(startOfMonth(addMonths(new Date(), 1)), new Date()) + 1;
  const showCredit =
    !!(company.lunchCreditAmount || !!foodUser?.currentCreditBalance) &&
    !userHasFreeDeal;
  const balanceLowButNotCritical = hasLowBalance && !hasCriticalBalance;
  const subsidyPercentage = !usersDeals
    ? null
    : usersDeals.length === 1 ||
        (usersDeals.length === 2 &&
          usersDeals[0].subsidyPercentage &&
          usersDeals[0].subsidyPercentage === usersDeals[1].subsidyPercentage)
      ? usersDeals[0].subsidyPercentage || null
      : null;

  useEffect(() => {
    if (hasPendingTransactions) {
      setTopupTooltipOpen(true);
      let timeoutId: NodeJS.Timeout;
      let delay = 1000;

      // Continually refetch food user until pending transactions are cleared (with exponential backoff)
      const poll = () => {
        timeoutId = setTimeout(() => {
          refetchFoodUser();
          delay *= 2;
          poll();
        }, delay);
      };

      poll();

      return () => clearTimeout(timeoutId);
    } else {
      setTopupTooltipOpen(false);
    }
  }, [hasPendingTransactions, refetchFoodUser]);

  useEffect(() => {
    if (!isLoadingFoodUser && !foodUser) {
      // If foodUser is not found and is not loading, trigger error boundary
      throw new Error("Food system unavailable");
    }
  }, [foodUser, isLoadingFoodUser]);

  const PageHeader = () => (
    <div className="pageHeader">
      <h1>{t("SIDEBAR.LUNCH")}</h1>
      <p>{t("PAGES.LUNCH.SUBHEADER")}</p>
    </div>
  );

  return (
    <>
      <PageBreadcrumbs
        levels={[{ label: t("SIDEBAR.LUNCH"), path: "/lunch" }]}
      />
      {isLoadingDeals || isLoadingFoodUser ? (
        <>
          <PageHeader />
          <div className="center">
            <LoadingSpinner />
          </div>
        </>
      ) : !foodUser?.history?.length &&
        !foodUser?.pendingTransactions?.length &&
        !introDismissed ? (
        <div>
          <div
            style={{
              margin: "var(--wlcm-spacing-lg) auto 300px",
              width: "100%",
              maxWidth: 600,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--wlcm-spacing-sm)",
            }}
          >
            <Icon
              name="restaurant"
              style={{ fontSize: "5rem", opacity: 0.5 }}
            />
            <div style={{ textAlign: "center" }}>
              <h1 style={{ opacity: 0.5 }}>
                {t("PAGES.LUNCH.ONBOARDING.HEADER")}
              </h1>
              <p>{t("PAGES.LUNCH.ONBOARDING.SUBHEADER")}</p>
            </div>
            <Stepper
              variant="vertical"
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              steps={[
                ...(!userHasFreeDeal
                  ? [
                      {
                        step: 1,
                        icon: "money",
                        title: t("PAGES.LUNCH.ONBOARDING.STEP_1_TITLE"),
                        content: (
                          <>
                            <p>
                              <strong
                                style={{
                                  color: "var(--wlcm-color-malling-purple)",
                                }}
                              >
                                {t("PAGES.LUNCH.ONBOARDING.STEP_1_BODY_BOLD")}
                              </strong>
                              {t("PAGES.LUNCH.ONBOARDING.STEP_1_BODY")}
                            </p>
                            {!!company.lunchCreditAmount && (
                              <p
                                style={{
                                  margin:
                                    "var(--wlcm-spacing-sm) 0 var(--wlcm-spacing-xs)",
                                }}
                              >
                                <strong
                                  style={{
                                    color: "var(--wlcm-color-malling-purple)",
                                  }}
                                >
                                  {t(
                                    "PAGES.LUNCH.ONBOARDING.STEP_1_CREDIT_BOLD",
                                    {
                                      amount: NOK.format(
                                        company.lunchCreditAmount,
                                      ),
                                    },
                                  )}
                                </strong>{" "}
                                {t("PAGES.LUNCH.ONBOARDING.STEP_1_CREDIT")}
                              </p>
                            )}
                          </>
                        ),
                      },
                    ]
                  : []),
                {
                  step: userHasFreeDeal ? 1 : 2,
                  icon: "qr_code",
                  title: t("PAGES.LUNCH.ONBOARDING.STEP_2_TITLE"),
                  content: (
                    <>
                      <p>
                        <strong
                          style={{ color: "var(--wlcm-color-malling-purple)" }}
                        >
                          {t("PAGES.LUNCH.ONBOARDING.STEP_2_BODY_BOLD")}
                        </strong>{" "}
                        {t("PAGES.LUNCH.ONBOARDING.STEP_2_BODY")}
                      </p>
                      <div
                        style={{
                          margin:
                            "var(--wlcm-spacing-md) auto var(--wlcm-spacing-sm)",
                          width: "200px",
                          height: "220px",
                          background:
                            "linear-gradient(135deg, #b48ace, #7092eb, #67a5d3)",
                          padding: "var(--wlcm-spacing-sm)",
                          borderRadius: "var(--wlcm-border-radius-sm)",
                          position: "relative",
                        }}
                      >
                        <h3 style={{ opacity: 0.5, textAlign: "center" }}>
                          {t("PAGES.LUNCH.LUNCH_CARD")}
                        </h3>
                        <div
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            opacity: 0.25,
                          }}
                        >
                          <Icon name="qr_code" style={{ fontSize: 200 }} />
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "max-content",
                          }}
                        >
                          {isMobile ? (
                            <AddToWalletButton
                              useQueryOptions={foodWalletBadgeOptions(
                                user.userId,
                              )}
                            />
                          ) : (
                            <Button
                              icon="digital_wallet"
                              dark
                              onClick={(e) => {
                                e.stopPropagation();
                                setWalletModalOpen(true);
                              }}
                            >
                              {t("PAGES.LUNCH.ADD_TO_WALLET")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  ),
                },
                {
                  step: userHasFreeDeal ? 2 : 3,
                  icon: "restaurant",
                  title: t("PAGES.LUNCH.ONBOARDING.STEP_3_TITLE"),
                  content: (
                    <>
                      {usersDeals?.length === 2 && (
                        <p style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>
                          {t(
                            "PAGES.LUNCH.ONBOARDING.STEP_3_BODY_MULTI_VARIANT",
                            {
                              variant1: usersDeals[0]?.lunchVariant?.name,
                              variant2: usersDeals[1]?.lunchVariant?.name,
                            },
                          )}
                        </p>
                      )}
                      <p>
                        {t("PAGES.LUNCH.ONBOARDING.STEP_3_BODY_1")}
                        <strong
                          style={{ color: "var(--wlcm-color-malling-purple)" }}
                        >
                          {" "}
                          {usersDeals?.length === 2 &&
                            t(
                              "PAGES.LUNCH.ONBOARDING.STEP_3_BODY_MULTI_VARIANT_BOLD",
                            )}
                          {t("PAGES.LUNCH.ONBOARDING.STEP_3_BODY_BOLD")}
                        </strong>
                        {t(
                          `PAGES.LUNCH.ONBOARDING.STEP_3_BODY_2${userHasFreeDeal ? "_FREE_DEAL" : ""}`,
                        )}
                      </p>
                      {usersDeals && (
                        <p
                          style={{
                            margin: "var(--wlcm-spacing-sm) 0",
                            fontSize: "var(--wlcm-text-sm)",
                          }}
                        >
                          <strong>
                            {t("PAGES.LUNCH.ONBOARDING.STEP_3_PS_BOLD")}{" "}
                            <span style={{ opacity: 0.5 }}>
                              {t(
                                `PAGES.LUNCH.ONBOARDING.STEP_3_PS${userHasFreeDeal ? "_FREE_DEAL" : usersDeals.length === 2 ? "_TWO_VARIANTS" : ""}`,
                                {
                                  price: NOK.format(
                                    usersDeals?.[0]?.price ?? 0,
                                  ),
                                  price2: NOK.format(
                                    usersDeals?.[1]?.price ?? 0,
                                  ),
                                  variant1: usersDeals?.[0]?.lunchVariant?.name,
                                  variant2: usersDeals?.[1]?.lunchVariant?.name,
                                },
                              )}
                            </span>
                          </strong>
                        </p>
                      )}
                    </>
                  ),
                },
                {
                  step: userHasFreeDeal ? 3 : 4,
                  icon: "done",
                  title: t("PAGES.LUNCH.ONBOARDING.STEP_4_TITLE"),
                  content: (
                    <>
                      <div
                        style={{
                          textAlign: "center",
                          marginBottom: "var(--wlcm-spacing-sm)",
                        }}
                      >
                        <h3
                          style={{ color: "var(--wlcm-color-malling-purple)" }}
                        >
                          {t("PAGES.LUNCH.ONBOARDING.STEP_4_HEADER")}
                        </h3>
                        <p>
                          {t(
                            `PAGES.LUNCH.ONBOARDING.STEP_4_BODY${userHasFreeDeal ? "_FREE_DEAL" : ""}`,
                          )}
                        </p>
                        {userHasFreeDeal && (
                          <Button
                            purple
                            style={{ margin: "var(--wlcm-spacing-sm) auto" }}
                            onClick={() => setIntroDismissed(true)}
                          >
                            {t(
                              "PAGES.LUNCH.ONBOARDING.DISMISS_BUTTON_FREE_DEAL",
                            )}
                          </Button>
                        )}
                      </div>
                      {!userHasFreeDeal && (
                        <>
                          <BalanceTopup
                            singleLunchPrice={usersMostExpensiveDealPrice ?? 0}
                          />
                          <Button
                            secondary
                            style={{
                              margin: "var(--wlcm-spacing-md) 0 0 auto",
                            }}
                            onClick={() => setIntroDismissed(true)}
                          >
                            {t("PAGES.LUNCH.ONBOARDING.DISMISS_BUTTON")}
                          </Button>
                        </>
                      )}
                    </>
                  ),
                },
              ]}
              text={{
                step: t("STEP"),
                next: t("NEXT_STEP"),
                prev: t("PREVIOUS_STEP"),
              }}
              containerStyle={{
                width: "100%",
              }}
              buttons={{
                prev: <></>,
              }}
            />
          </div>
        </div>
      ) : usersDeals?.length ? (
        <>
          <PageHeader />
          <div className={styles.lunchCardsGrid}>
            <UsersLunchQRCard />
            {!!(!userHasFreeDeal || foodUser?.currentBalance) &&
              (showCredit ? (
                <div
                  className={`${styles.lunchBalanceCard} ${styles.creditVariant} ${companySettingsStyles.numberCard} ${
                    hasCriticalBalance
                      ? `${companySettingsStyles.red} ${styles.red}`
                      : hasLowBalance
                        ? `${companySettingsStyles.orange} ${styles.orange}`
                        : ""
                  }`}
                >
                  <div
                    className={`${styles.creditContainer} ${!foodUser?.currentCreditBalance ? styles.noCreditLeft : ""}`}
                  >
                    <p>
                      {t("PAGES.LUNCH.CREDIT.LABEL")}{" "}
                      <Icon
                        name="info"
                        id="credit-info"
                        style={{
                          fontSize: "var(--wlcm-text-md)",
                          verticalAlign: "text-top",
                          marginLeft: "var(--wlcm-spacing-xs)",
                          opacity: 0.5,
                        }}
                      />
                    </p>
                    <Tooltip
                      anchorSelect="#credit-info"
                      content={t("PAGES.LUNCH.CREDIT.TOOLTIP")}
                      style={{
                        width: 200,
                        textAlign: "left",
                        zIndex: 10,
                      }}
                    />
                    <p>
                      <strong>
                        <AnimatedTickingNumber
                          to={foodUser?.currentCreditBalance}
                          formatter={NOK.format}
                        />
                      </strong>
                    </p>
                    <div style={{ marginTop: "var(--wlcm-spacing-xs)" }}>
                      <ProgressIndicator
                        id="credit-amount"
                        label={t("PAGES.LUNCH.CREDIT.REMAINING")}
                        hideLabel
                        value={foodUser?.currentCreditBalance || 0}
                        total={company.lunchCreditAmount}
                        trackHeight={10}
                        trackColor={
                          !foodUser?.currentCreditBalance ||
                          balanceLowButNotCritical
                            ? "rgba(0,0,0,0.1)"
                            : "rgba(255,255,255,0.1)"
                        }
                        barColor={
                          balanceLowButNotCritical
                            ? "var(--wlcm-color-light-black)"
                            : "white"
                        }
                      />
                      <p
                        style={{
                          fontSize: "var(--wlcm-text-xs)",
                          opacity: 0.75,
                          marginTop: 3,
                        }}
                      >
                        {daysUntilNextMonth === 1
                          ? t("PAGES.LUNCH.CREDIT.RENEWAL_TOMORROW")
                          : t("PAGES.LUNCH.CREDIT.RENEWAL_IN_X_DAYS", {
                              count: daysUntilNextMonth,
                            })}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`${styles.balanceContainer} ${foodUser?.currentCreditBalance ? styles.hasCredit : ""}`}
                  >
                    <p>
                      {t("PAGES.LUNCH.BALANCE")}
                      <span className="sr-only">: </span>
                    </p>
                    <p>
                      <strong className="user-balance-number">
                        <AnimatedTickingNumber
                          to={foodUser?.currentBalance}
                          formatter={NOK.format}
                        />
                      </strong>
                    </p>
                    <Button
                      {...(hasCriticalBalance
                        ? { danger: true }
                        : hasLowBalance
                          ? { warning: true }
                          : foodUser?.currentCreditBalance
                            ? {
                                className: styles.greyTopupButton,
                              }
                            : { success: true })}
                      icon="money"
                      onClick={() => setTopupModalOpen(true)}
                      style={{ marginTop: "var(--wlcm-spacing-xs)" }}
                    >
                      {t("PAGES.LUNCH.TOP_UP")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={`${styles.lunchBalanceCard} ${companySettingsStyles.numberCard} ${userHasFreeDeal ? companySettingsStyles.gray : hasCriticalBalance ? companySettingsStyles.red : hasLowBalance ? companySettingsStyles.orange : ""}`}
                >
                  {!userHasFreeDeal && (
                    <Button
                      {...(hasCriticalBalance
                        ? { danger: true }
                        : hasLowBalance
                          ? { warning: true }
                          : { success: true })}
                      icon="money"
                      onClick={() => setTopupModalOpen(true)}
                      style={{ marginTop: "var(--wlcm-spacing-xs)" }}
                    >
                      {t("PAGES.LUNCH.TOP_UP")}
                    </Button>
                  )}
                  {t("PAGES.LUNCH.BALANCE")}
                  {/* TODO: Maybe add some explanation here for if a user had an existing balance but now has a free deal. Explaining they will get their money back at some point? */}
                  <span className="sr-only">: </span>
                  <strong className="user-balance-number">
                    <AnimatedTickingNumber
                      to={foodUser?.currentBalance ?? 0}
                      formatter={NOK.format}
                    />
                  </strong>
                </div>
              ))}
            <Tooltip
              anchorSelect=".user-balance-number"
              isOpen={topupTooltipOpen}
              closeEvents={{
                mouseleave: true,
              }}
              openEvents={{
                mouseover: false,
              }}
              setIsOpen={setTopupTooltipOpen}
              content={t("PAGES.LUNCH.BALANCE_TOOLTIP")}
              style={{ maxWidth: 200 }}
            />
            <div
              className={`${styles.lunchDealCard} ${companySettingsStyles.numberCard} ${companySettingsStyles.purple}`}
              style={
                showCredit && !subsidyPercentage && usersDeals.length === 2
                  ? { flexDirection: "column" }
                  : {}
              }
            >
              <p>
                {t("PAGES.LUNCH.PRICE_PER_LUNCH")}
                {
                  /* Only display deal name if user has 1 deal, otherwise UI becomes messy */
                  usersDeals.length === 1 ? (
                    <>
                      <br />
                      <span style={{ opacity: 0.5 }}>
                        {t("PAGES.LUNCH.WITH_DEAL")}{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {usersDeals[0].name}
                        </span>
                      </span>
                      <span className="sr-only">: </span>
                    </>
                  ) : (
                    !!subsidyPercentage && (
                      <>
                        {" "}
                        <span style={{ opacity: 0.5 }}>*</span>
                        <br />
                        <span
                          style={{
                            opacity: 0.5,
                            fontSize: "var(--wlcm-text-sm)",
                          }}
                        >
                          <Trans
                            i18nKey="PAGES.LUNCH.WITH_SUBSIDY"
                            components={{
                              bold: <span style={{ fontWeight: "bold" }} />,
                            }}
                            values={{
                              subsidyPercentage,
                            }}
                          />
                        </span>
                      </>
                    )
                  )
                }
              </p>
              <div
                className={styles.multiLunchDealCardInner}
                style={{
                  gridTemplateColumns:
                    usersDeals.length > 1 ? "1fr auto 1fr" : "1fr",
                }}
              >
                <p>
                  <strong>
                    <AnimatedTickingNumber
                      to={calculateLunchPrice(
                        usersDeals[0].price,
                        usersDeals[0].subsidyPercentage,
                      )}
                      formatter={NOK.format}
                    />
                  </strong>
                  {
                    /* Only display variant name if user has more than 1 deal */
                    usersDeals.length > 1 && (
                      <>
                        <br />
                        <span className={styles.lunchVariantName}>
                          {usersDeals[0].lunchVariant?.name}
                        </span>
                      </>
                    )
                  }
                </p>
                {usersDeals.length > 1 && (
                  <>
                    <p>
                      <strong style={{ opacity: 0.3 }}>/</strong>
                    </p>
                    <p>
                      <strong>
                        <AnimatedTickingNumber
                          to={calculateLunchPrice(
                            usersDeals[1].price,
                            usersDeals[1].subsidyPercentage,
                          )}
                          formatter={NOK.format}
                        />
                      </strong>
                      <br />
                      <span className={styles.lunchVariantName}>
                        {usersDeals[1].lunchVariant?.name}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <UserTransactionsTable
            userId={user.userId}
            buildingTenantId={user.buildingTenantId}
          />
          <PopupCard
            onClose={() => setTopupModalOpen(false)}
            open={topupModalOpen && !userHasFreeDeal}
          >
            <h3 style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>
              {t("PAGES.LUNCH.TOPUP_MODAL_HEADER")}
            </h3>
            <BalanceTopup
              dark
              singleLunchPrice={usersMostExpensiveDealPrice ?? 0}
            />
          </PopupCard>
        </>
      ) : (
        foodUser && (
          <div
            className="center"
            style={{ textAlign: "center", maxWidth: 600 }}
          >
            <h1
              className="h3"
              style={{ color: "var(--wlcm-color-danger-red)" }}
            >
              {t("PAGES.LUNCH.NO_DEAL_HEADER")}
            </h1>
            <p>{t("PAGES.LUNCH.NO_DEAL_BODY")}</p>
          </div>
        )
      )}
      <PopupCard
        open={walletModalOpen && isDesktop}
        onClose={() => setWalletModalOpen(false)}
      >
        <Suspense
          fallback={
            <div className="center">
              <LoadingSpinner />
            </div>
          }
        >
          <WalletBadgeModalContent closeFn={() => setWalletModalOpen(false)} />
        </Suspense>
      </PopupCard>
    </>
  );
}
